// Page « Prêts de bornes » — suivi des bornes prêtées aux antennes.
// Remplace le tableur « PRÊT BORNE aux antennes » (date, antenne, nom,
// n° de borne, motif, code event, accès studio).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';
import { Pencil, Trash2, Plus, Search, Handshake } from 'lucide-react';
import {
  setAuthToken, getPretsBornes, getPretsBornesOptions, getAntennesList,
  createPretBorne, updatePretBorne, deletePretBorne, getGatewayUploadsUrl,
} from '../services/api';
import { Gate } from '../lib/perms';
import BornePicker from '../components/BornePicker';
import ContactPicker from '../components/ContactPicker';
import ConfirmModal from '../components/ConfirmModal';
import './PretsBornes.css';

const EMPTY_FORM = {
  date_pret: '',
  contact_antenne_id: '',
  antenne_id: '',
  nom: '',
  borne_id: '',
  borne_libelle: '',
  motif: '',
  code_event: '',
  acces_studio: '',
};

const formatDate = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
const todayIso = () => new Date().toISOString().slice(0, 10);

const formatDateTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

// Avatar rond d'un utilisateur (photo de son compte, sinon ses initiales) ;
// son nom apparaît au survol.
function UserAvatar({ prenom, nom, photo, title }) {
  const [broken, setBroken] = useState(false);
  const name = [prenom, nom].map(s => (s || '').trim()).filter(Boolean).join(' ');
  const initials = [prenom, nom].map(s => (s || '').trim().charAt(0).toUpperCase()).join('') || '?';
  // « Créé le … par Prénom Nom »
  const tip = `${title} par ${name || 'un utilisateur inconnu'}`;
  return (
    <span className="prets-avatar" title={tip}>
      {photo && !broken
        ? <img src={getGatewayUploadsUrl(photo)} alt={name} onError={() => setBroken(true)} />
        : initials}
    </span>
  );
}

// Nom affiché dans la colonne « Nom » : le nom saisi, sinon le contact.
const nomOf = (p) => p.nom || [p.contact_prenom, p.contact_nom].map(s => (s || '').trim()).filter(Boolean).join(' ');

export default function PretsBornes() {
  const auth = useAuth();
  const [prets, setPrets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [annee, setAnnee] = useState('');
  const [options, setOptions] = useState({ bornes: [], contacts: [] });
  const [antennes, setAntennes] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | prêt
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPrets(await getPretsBornes(annee ? { annee } : {}));
    } catch {
      toast.error('Impossible de charger les prêts');
    } finally {
      setLoading(false);
    }
  }, [annee]);

  useEffect(() => {
    if (!auth.user?.access_token) return;
    setAuthToken(auth.user.access_token);
    load();
  }, [auth.user?.access_token, load]);

  useEffect(() => {
    if (!auth.user?.access_token) return;
    setAuthToken(auth.user.access_token);
    getPretsBornesOptions().then(setOptions).catch(() => {});
    getAntennesList().then(setAntennes).catch(() => {});
  }, [auth.user?.access_token]);

  // Années proposées dans le filtre : de l'année en cours à 2024.
  const annees = useMemo(() => {
    const now = new Date().getFullYear();
    const list = [];
    for (let y = now + 1; y >= 2024; y--) list.push(y);
    return list;
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return prets;
    return prets.filter(p => [
      nomOf(p), p.antenne_ville, p.borne_numero, p.borne_libelle, p.motif, p.code_event, p.acces_studio,
    ].some(v => (v || '').toLowerCase().includes(q)));
  }, [prets, search]);


  const openNew = () => {
    setForm({ ...EMPTY_FORM, date_pret: todayIso() });
    setEditing('new');
  };

  const openEdit = (p) => {
    setForm({
      date_pret: p.date_pret || '',
      contact_antenne_id: p.contact_antenne_id ? String(p.contact_antenne_id) : '',
      antenne_id: p.antenne_id ? String(p.antenne_id) : '',
      nom: p.nom || '',
      borne_id: p.borne_id ? String(p.borne_id) : '',
      borne_libelle: p.borne_libelle || '',
      motif: p.motif || '',
      code_event: p.code_event || '',
      acces_studio: p.acces_studio || '',
    });
    setEditing(p);
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Choisir un contact reprend son antenne si aucune n'est encore saisie.
  const handleContactChange = (val) => {
    setForm(prev => {
      const c = options.contacts.find(x => String(x.id) === val);
      return {
        ...prev,
        contact_antenne_id: val,
        antenne_id: prev.antenne_id || (c?.antenne_id ? String(c.antenne_id) : ''),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date_pret) { toast.error('La date du prêt est obligatoire'); return; }
    if (!form.contact_antenne_id && !form.nom.trim()) {
      toast.error('Choisissez un contact ou saisissez un nom');
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await createPretBorne(form);
        toast.success('Prêt enregistré');
      } else {
        await updatePretBorne(editing.id, form);
        toast.success('Prêt modifié');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deletePretBorne(toDelete.id);
      toast.success('Prêt supprimé');
      setToDelete(null);
      load();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="prets-page">
      <div className="page-header prets-header">
        <div>
          <h1><Handshake size={22} /> Prêts de bornes</h1>
          <p className="prets-subtitle">Bornes prêtées aux antennes pour leurs événements personnels.</p>
        </div>
        <Gate perm="prets_bornes.create">
          <button type="button" className="btn btn-primary" onClick={openNew} data-perm="antennes:prets_bornes.create">
            <Plus size={16} /> Nouveau prêt
          </button>
        </Gate>
      </div>

      <div className="prets-toolbar">
        <div className="prets-search">
          <Search size={16} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (nom, antenne, borne, motif, code event…)"
          />
        </div>
        <select value={annee} onChange={(e) => setAnnee(e.target.value)}>
          <option value="">Toutes les années</option>
          {annees.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="prets-count">{filtered.length} prêt{filtered.length > 1 ? 's' : ''}</span>
      </div>

      <div className="card prets-card">
        {loading ? (
          <p className="prets-empty">Chargement…</p>
        ) : filtered.length === 0 ? (
          <p className="prets-empty">Aucun prêt{search || annee ? ' pour ces critères' : ' enregistré'}.</p>
        ) : (
          <table className="prets-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Antenne</th>
                <th>Nom</th>
                <th>N° borne</th>
                <th>Motif</th>
                <th>Code event</th>
                <th>Accès Studio</th>
                <th>Créé / modifié</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id}>
                  <td className="nowrap">{formatDate(p.date_pret)}</td>
                  <td><strong>{p.antenne_ville || '—'}</strong></td>
                  <td>
                    {p.contact_antenne_id ? (
                      <Link to={`/contacts/${p.contact_antenne_id}`}>{nomOf(p) || '—'}</Link>
                    ) : (nomOf(p) || '—')}
                  </td>
                  <td className="nowrap">
                    {p.borne_numero || p.borne_libelle || '—'}
                    {p.borne_numero_estime && (
                      <span className="prets-estime" title="Numéro recomposé : il manque dans l'app bornes">⚠ estimé</span>
                    )}
                    {p.borne_model && <span className="prets-muted"> · {p.borne_model}</span>}
                  </td>
                  <td>{p.motif || ''}</td>
                  <td className="nowrap">{p.code_event || ''}</td>
                  <td>{p.acces_studio || ''}</td>
                  <td className="prets-suivi">
                    <UserAvatar
                      prenom={p.created_by_prenom} nom={p.created_by_nom} photo={p.created_by_photo}
                      title={`Créé le ${formatDateTime(p.created)}`}
                    />
                    {p.modified_by && (
                      <>
                        <UserAvatar
                          prenom={p.modified_by_prenom} nom={p.modified_by_nom} photo={p.modified_by_photo}
                          title={`Modifié le ${formatDateTime(p.modified)}`}
                        />
                        <span className="prets-muted prets-suivi-date">modifié le {formatDateTime(p.modified)}</span>
                      </>
                    )}
                  </td>
                  <td className="prets-actions">
                    <Gate perm="prets_bornes.edit">
                      <button type="button" className="icon-btn" title="Modifier" onClick={() => openEdit(p)} data-perm="antennes:prets_bornes.edit">
                        <Pencil size={15} />
                      </button>
                    </Gate>
                    <Gate perm="prets_bornes.delete">
                      <button type="button" className="icon-btn danger" title="Supprimer" onClick={() => setToDelete(p)} data-perm="antennes:prets_bornes.delete">
                        <Trash2 size={15} />
                      </button>
                    </Gate>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="modal-content prets-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing === 'new' ? 'Nouveau prêt de borne' : 'Modifier le prêt'}</h3>
              <button type="button" className="modal-close" onClick={() => setEditing(null)} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body prets-form">
                <div className="form-group">
                  <label>Date du prêt *</label>
                  <input type="date" value={form.date_pret} onChange={(e) => setField('date_pret', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Contact</label>
                  <ContactPicker
                    contacts={options.contacts}
                    value={form.contact_antenne_id}
                    onChange={handleContactChange}
                  />
                </div>
                <div className="form-group">
                  <label>Nom</label>
                  <input
                    type="text"
                    value={form.nom}
                    onChange={(e) => setField('nom', e.target.value)}
                    placeholder={form.contact_antenne_id ? 'Si différent du contact (ex. « Léo, fils d\'Eddy »)' : 'Nom du bénéficiaire'}
                  />
                </div>
                <div className="form-group">
                  <label>Antenne</label>
                  <select value={form.antenne_id} onChange={(e) => setField('antenne_id', e.target.value)}>
                    <option value="">—</option>
                    {antennes.map(a => <option key={a.id} value={a.id}>{a.ville_principale}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Borne</label>
                  <BornePicker
                    placeholder="Rechercher une borne (numéro, contact, ville…)"
                    bornes={options.bornes}
                    value={form.borne_id}
                    onChange={(val) => setField('borne_id', val)}
                    preferredContactId={form.contact_antenne_id}
                  />
                </div>
                <div className="form-group">
                  <label>Motif</label>
                  <input type="text" value={form.motif} onChange={(e) => setField('motif', e.target.value)} placeholder="ex. anniversaire, mariage…" />
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Code event</label>
                    <input type="text" value={form.code_event} onChange={(e) => setField('code_event', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Accès Studio</label>
                    <input type="text" value={form.acces_studio} onChange={(e) => setField('acces_studio', e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toDelete && (
        <ConfirmModal
          titre="Supprimer ce prêt ?"
          message={`Prêt du ${formatDate(toDelete.date_pret)} — ${nomOf(toDelete) || 'sans nom'}`}
          detail="Le prêt disparaîtra de la liste."
          libelleConfirmer="Supprimer"
          danger
          onConfirm={confirmDelete}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  );
}
